import {
  pathOr,
  map,
  compose,
  uniq,
  innerJoin,
  flatten,
  contains,
  equals,
  path,
  filter,
} from 'ramda';
import ProtektorResourceModel from '../models/protektor-resource';
import ProtektorRolesModel from '../models/protektor-roles';
import { handleGeneralError } from '../graphql/errors';

class ProtektorMongoAdapter {
  roleIdentifierComparator;

  // Interface methods
  registerRoleIdentifierComparator = (predicate) => {
    this.roleIdentifierComparator = predicate;
  };

  findDataModels = (resource) => {
    const ProtektorResource = ProtektorResourceModel();
    return ProtektorResource.findDataModels(resource);
  };

  insertDataModels = (resource, models) => {
    const ProtektorResource = ProtektorResourceModel();
    return ProtektorResource.insertDataModels(resource, models);
  };

  insertAllow = (action, resourceName, roleIdentifier) => {
    const ProtektorRoles = ProtektorRolesModel();
    return ProtektorRoles.insertAllow(action, resourceName, roleIdentifier);
  };

  insertForbid = (action, resourceName, roleIdentifier) => {
    const ProtektorRoles = ProtektorRolesModel();
    return ProtektorRoles.insertForbid(action, resourceName, roleIdentifier);
  };

  removePermission = (action, resource, roleIdentifier) => {
    const ProtektorRoles = ProtektorRolesModel();
    return ProtektorRoles.removePermission(action, resource, roleIdentifier);
  };

  removeRole = (roleIdentifier) => {
    const ProtektorRoles = ProtektorRolesModel();
    return ProtektorRoles.removeRole(roleIdentifier);
  };

  hasModel = async (modelName, roleIdentifier) => {
    const ProtektorRoles = ProtektorRolesModel();
    let role;

    try {
      role = await ProtektorRoles.findRole(roleIdentifier);
    } catch (error) {
      return handleGeneralError(error);
    }

    const ProtektorResource = ProtektorResourceModel();
    let allResources;

    try {
      allResources = await ProtektorResource.findAllResources();
    } catch (error) {
      return handleGeneralError(error);
    }

    const permissions = pathOr([], ['permissions'], role);
    const listOfResources = map(pathOr([], ['resource']));
    const uniqueResources = compose(
      uniq,
      listOfResources,
    );
    const resourcesOwnedByRole = uniqueResources(permissions);
    const modelsOwnedByResources = innerJoin(
      (resource, resourceName) => resource.resource === resourceName,
      allResources,
    );
    const modelsOwnedByRole = compose(
      flatten,
      map(pathOr([], ['models'])),
      modelsOwnedByResources,
    );

    return Promise.resolve(
      contains(modelName, modelsOwnedByRole(resourcesOwnedByRole)),
    );
  };

  findModel = async (modelName, roleIdentifier) => {
    const ProtektorRoles = ProtektorRolesModel();
    let role;

    try {
      role = await ProtektorRoles.findRole(roleIdentifier);
    } catch (error) {
      return handleGeneralError(error);
    }

    const ProtektorResource = ProtektorResourceModel();
    let allResources;

    try {
      allResources = await ProtektorResource.findAllResources();
    } catch (error) {
      return handleGeneralError(error);
    }

    const permissions = pathOr([], ['permissions'], role);
    const listOfResources = map(pathOr([], ['resource']));
    const uniqueResources = compose(
      uniq,
      listOfResources,
    );
    const resourcesOwnedByRole = uniqueResources(permissions);
    const modelsOwnedByResources = innerJoin(
      (resource, resourceName) => resource.resource === resourceName,
      allResources,
    );
    const modelsOwnedByRole = compose(
      flatten,
      map(pathOr([], ['models'])),
      modelsOwnedByResources,
    );

    if (contains(modelName, modelsOwnedByRole(resourcesOwnedByRole))) {
      return Promise.resolve(modelName);
    }

    return Promise.resolve();
  };

  hasPermission = async (action, resource, roleIdentifier) => {
    const ProtektorRoles = ProtektorRolesModel();
    let role;

    try {
      role = await ProtektorRoles.findRole(roleIdentifier);
    } catch (error) {
      return handleGeneralError(error);
    }

    const permissions = pathOr([], ['permissions']);
    const isAllowed = compose(
      contains({ action, resource, allowed: true }),
      permissions,
    );
    return Promise.resolve(isAllowed(role));
  };

  findAllResourceNames = async () => {
    const ProtektorResource = ProtektorResourceModel();
    const resources = await ProtektorResource.findAllResources();
    return Promise.resolve(map(pathOr([], ['resource']), resources));
  };

  filterRoles = async (filterComparator, roleIdentifier) => {
    const ProtektorRoles = ProtektorRolesModel();
    let roles;

    try {
      roles = await ProtektorRoles.findAllRoles();
    } catch (error) {
      return handleGeneralError(error);
    }

    return filter(
      compose(
        equals(filterComparator(roleIdentifier)),
        filterComparator,
        path(['roleIdentifier']),
      ),
      roles,
    );
  };

  findRole = async (roleIdentifier) => {
    const ProtektorRoles = ProtektorRolesModel();
    let role;

    try {
      role = await ProtektorRoles.findRole(roleIdentifier);
    } catch (error) {
      return handleGeneralError(error);
    }

    return Promise.resolve(role);
  };
}

export default ProtektorMongoAdapter;
